import * as React from 'react';
import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive, AlertDialog as AlertDialogPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';
import Button from '@/components/ui/button';
import buttonVariants from '@/components/ui/button-variants';

const overlayClasses =
  'fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0';

const contentClasses =
  'fixed z-50 flex w-full flex-col gap-4 bg-background shadow-lg outline-none duration-200 ' +
  // Mobile: bottom-anchored full-bleed sheet with rounded top and safe-area bottom
  'inset-x-0 bottom-0 max-h-[90dvh] overflow-y-auto rounded-t-2xl border-t p-6 pb-safe ' +
  // Desktop: centred modal, matches shadcn Dialog styling
  'lg:inset-x-auto lg:bottom-auto lg:left-1/2 lg:top-1/2 lg:max-w-lg lg:max-h-[85vh] lg:-translate-x-1/2 lg:-translate-y-1/2 ' +
  'lg:rounded-2xl lg:border lg:p-6 lg:pb-6 ' +
  // Animation: slide from bottom on mobile, fade + zoom on desktop
  'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom ' +
  'lg:data-[state=closed]:slide-out-to-bottom-0 lg:data-[state=closed]:fade-out-0 lg:data-[state=closed]:zoom-out-95 ' +
  'lg:data-[state=open]:slide-in-from-bottom-0 lg:data-[state=open]:fade-in-0 lg:data-[state=open]:zoom-in-95';

function Grabber() {
  return (
    <div
      aria-hidden='true'
      className='mx-auto mt-0 mb-2 h-1.5 w-10 shrink-0 rounded-full bg-neutral-300 lg:hidden'
    />
  );
}

const CloseButtonIcon = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function CloseButtonIcon(props, ref) {
  return (
    <button
      {...props}
      ref={ref}
      type='button'
      className={cn(
        'absolute top-3 right-3 rounded-full p-2 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden',
        props.className,
      )}
    >
      <XIcon className='size-5' />
      <span className='sr-only'>Close</span>
    </button>
  );
});

// ---------------------------------------------------------------------------
// Sheet — Radix Dialog-based. Informational; dismisses on ESC / outside click.
// ---------------------------------------------------------------------------

function Sheet({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot='sheet' {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot='sheet-trigger' {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot='sheet-portal' {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot='sheet-close' {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot='sheet-overlay'
      className={cn(overlayClasses, className)}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        data-slot='sheet-content'
        className={cn(contentClasses, className)}
        {...props}
      >
        <Grabber />
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close asChild>
            <CloseButtonIcon />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-header'
      className={cn('flex flex-col gap-2 text-left', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-footer'
      className={cn(
        'flex flex-col-reverse gap-2 lg:flex-row lg:justify-end',
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot='sheet-title'
      className={cn('text-lg font-semibold', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot='sheet-description'
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// ConfirmSheet — Radix AlertDialog-based. Blocking; requires explicit action.
// ---------------------------------------------------------------------------

function ConfirmSheet({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot='confirm-sheet' {...props} />;
}

function ConfirmSheetTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return (
    <AlertDialogPrimitive.Trigger
      data-slot='confirm-sheet-trigger'
      {...props}
    />
  );
}

function ConfirmSheetPortal({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal data-slot='confirm-sheet-portal' {...props} />
  );
}

function ConfirmSheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot='confirm-sheet-overlay'
      className={cn(overlayClasses, className)}
      {...props}
    />
  );
}

function ConfirmSheetContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <ConfirmSheetPortal>
      <ConfirmSheetOverlay />
      <AlertDialogPrimitive.Content
        data-slot='confirm-sheet-content'
        className={cn(contentClasses, className)}
        {...props}
      >
        <Grabber />
        {children}
      </AlertDialogPrimitive.Content>
    </ConfirmSheetPortal>
  );
}

function ConfirmSheetHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='confirm-sheet-header'
      className={cn('flex flex-col gap-2 text-left', className)}
      {...props}
    />
  );
}

function ConfirmSheetFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='confirm-sheet-footer'
      className={cn(
        'flex flex-col-reverse gap-2 lg:flex-row lg:justify-end',
        className,
      )}
      {...props}
    />
  );
}

function ConfirmSheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot='confirm-sheet-title'
      className={cn('text-lg font-semibold', className)}
      {...props}
    />
  );
}

function ConfirmSheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot='confirm-sheet-description'
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

function ConfirmSheetAction({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> &
  Pick<React.ComponentProps<typeof Button>, 'variant' | 'size'>) {
  return (
    <AlertDialogPrimitive.Action
      data-slot='confirm-sheet-action'
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

function ConfirmSheetCancel({
  className,
  variant = 'outline',
  size = 'default',
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> &
  Pick<React.ComponentProps<typeof Button>, 'variant' | 'size'>) {
  return (
    <AlertDialogPrimitive.Cancel
      data-slot='confirm-sheet-cancel'
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetPortal,
  SheetClose,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  ConfirmSheet,
  ConfirmSheetTrigger,
  ConfirmSheetPortal,
  ConfirmSheetOverlay,
  ConfirmSheetContent,
  ConfirmSheetHeader,
  ConfirmSheetFooter,
  ConfirmSheetTitle,
  ConfirmSheetDescription,
  ConfirmSheetAction,
  ConfirmSheetCancel,
};
